import { Quiz } from "../../../data-types/Types";

export type QuizTreeNode = {
  quiz: Quiz;
  children: QuizTreeNode[];
};

const compareQuizNames = (left?: Quiz, right?: Quiz) => {
  const leftName = left?.name?.toLowerCase() || "";
  const rightName = right?.name?.toLowerCase() || "";
  if (leftName < rightName) {
    return -1;
  }
  if (leftName > rightName) {
    return 1;
  }
  const leftId = left?.id || "";
  const rightId = right?.id || "";
  return leftId.localeCompare(rightId);
};

export const buildQuizMap = (quizzes: Quiz[]) => {
  const quizMap = new Map<string, Quiz>();
  quizzes.forEach((quiz) => {
    if (quiz.id) {
      quizMap.set(quiz.id, quiz);
    }
  });
  return quizMap;
};

export const collectTransitiveSubquizIds = (
  quizMap: Map<string, Quiz>,
  quizId: string
) => {
  const queuedIds = [quizId];
  const visitedIds = new Set<string>();
  const collectedIds: string[] = [];

  while (queuedIds.length > 0) {
    const nextId = queuedIds.pop();
    if (!nextId || visitedIds.has(nextId)) {
      continue;
    }
    visitedIds.add(nextId);
    const quiz = quizMap.get(nextId);
    if (!quiz) {
      continue;
    }
    if (nextId !== quizId) {
      collectedIds.push(nextId);
    }
    queuedIds.push(...(quiz.subquizIDs || []));
  }

  return collectedIds;
};

export const countTransitiveSubquizzes = (
  quizMap: Map<string, Quiz>,
  quizId: string
) => collectTransitiveSubquizIds(quizMap, quizId).length;

export const buildTopLevelQuizOrder = (quizzes: Quiz[]) => {
  const quizMap = buildQuizMap(quizzes);
  const quizCounts = new Map<string, number>();

  quizzes.forEach((quiz) => {
    if (quiz.id) {
      quizCounts.set(quiz.id, countTransitiveSubquizzes(quizMap, quiz.id));
    }
  });

  const sortedQuizzes = [...quizzes].sort((left, right) => {
    const leftCount = left.id ? quizCounts.get(left.id) || 0 : 0;
    const rightCount = right.id ? quizCounts.get(right.id) || 0 : 0;
    if (leftCount !== rightCount) {
      return rightCount - leftCount;
    }
    return compareQuizNames(left, right);
  });

  const seenSubquizIds = new Set<string>();
  const topLevelQuizzes: Quiz[] = [];

  sortedQuizzes.forEach((quiz) => {
    if (!quiz.id || seenSubquizIds.has(quiz.id)) {
      return;
    }
    topLevelQuizzes.push(quiz);
    const subquizIds = collectTransitiveSubquizIds(quizMap, quiz.id);
    subquizIds.forEach((subquizId) => seenSubquizIds.add(subquizId));
  });

  return { quizMap, topLevelQuizzes };
};

export const buildQuizTree = (
  quizMap: Map<string, Quiz>,
  quizId: string
): QuizTreeNode | null => {
  const rootQuiz = quizMap.get(quizId);
  if (!rootQuiz) {
    return null;
  }
  const visitedIds = new Set<string>();

  const buildNode = (quiz: Quiz): QuizTreeNode => {
    if (!quiz.id || visitedIds.has(quiz.id)) {
      return { quiz, children: [] };
    }
    visitedIds.add(quiz.id);
    const children = (quiz.subquizIDs || [])
      .map((childId) => quizMap.get(childId))
      .filter((child): child is Quiz => !!child)
      .map((child) => buildNode(child));
    return { quiz, children };
  };

  return buildNode(rootQuiz);
};
